import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  Chip,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { PatternFormat } from 'react-number-format';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { AvailabilityRequestService } from '../../services/availabilityRequest/AvailabilityRequestService';
import { EventService } from '../../services/event/EventService';
import { UserService } from '../../services/user/UserService';

interface AvailabilityRequestModalProps {
  open: boolean;
  onClose: () => void;
  espacos: any[];
  setSearchParams?: (params: URLSearchParams) => void;
  filterNames: {
    cidade: string;
    regiao: string;
    bairro: string;
    evento: string;
    espaco: string;
  };
  searchParams: any;
}

const AvailabilityRequestModal: React.FC<AvailabilityRequestModalProps> = ({
  open,
  onClose,
  espacos,
  setSearchParams,
  filterNames,
  searchParams,
}) => {
  const searchParamsDebounceRef = useRef<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Form states
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userWhatsApp, setUserWhatsApp] = useState('');
  const [description, setDescription] = useState('');
  const [invitesInput, setInvitesInput] = useState(
    searchParams.get('convidados') ? String(searchParams.get('convidados')) : '',
  );
  const [eventDateInput, setEventDateInput] = useState(
    searchParams.get('dataHora') ? dayjs(searchParams.get('dataHora')).format('YYYY-MM-DD') : '',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  const [eventos, setEventos] = useState<any[]>([]);
  const [selectedEvento, setSelectedEvento] = useState<any>(null);

  const [errors, setErrors] = useState({
    userName: '',
    userEmail: '',
    userWhatsApp: '',
    eventDate: '',
    eventType: '',
    invites: '',
    acceptTerms: '',
  });

  useEffect(() => {
    EventService.search()
      .then((response) => {
        setEventos(response.data);
      })
      .catch((error) => {
        console.error('Erro ao buscar eventos:', error);
      });
  }, [filterNames.evento, open]);

  useEffect(() => {
    const filterDateValue = searchParams.get('dataHora');
    if (filterDateValue) {
      setEventDateInput(dayjs(filterDateValue).format('YYYY-MM-DD'));
    }
  }, [searchParams]);

  useEffect(() => {
    const convidadosValue = searchParams.get('convidados');
    if (convidadosValue !== null) {
      setInvitesInput(String(convidadosValue));
    }
  }, [searchParams]);

  useEffect(() => {
    const searchEventId = searchParams.get('evento');

    if (!searchEventId) {
      setSelectedEvento(null);
      return;
    }

    const normalizedSearchEventId = Number(searchEventId);
    if (!Number.isFinite(normalizedSearchEventId)) {
      setSelectedEvento(null);
      return;
    }

    const matchedEvento =
      eventos.find((evento) => Number(evento?.id) === normalizedSearchEventId) || null;

    setSelectedEvento((prev) => {
      const prevId = prev?.id !== undefined && prev?.id !== null ? Number(prev.id) : null;
      const nextId =
        matchedEvento?.id !== undefined && matchedEvento?.id !== null
          ? Number(matchedEvento.id)
          : null;

      return prevId === nextId ? prev : matchedEvento;
    });
  }, [eventos, searchParams]);

  useEffect(() => {
    return () => {
      if (searchParamsDebounceRef.current !== null) {
        window.clearTimeout(searchParamsDebounceRef.current);
      }
    };
  }, []);

  const updateSearchParamsDebounced = (updater: (params: URLSearchParams) => void) => {
    if (!setSearchParams) return;

    const params = new URLSearchParams(searchParams);
    updater(params);

    if (searchParamsDebounceRef.current !== null) {
      window.clearTimeout(searchParamsDebounceRef.current);
    }

    searchParamsDebounceRef.current = window.setTimeout(() => {
      setSearchParams(params);
    }, 400);
  };

  useEffect(() => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const trimmedEmail = userEmail.trim();

    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
      setEmailExists(null);
      setEmailCheckLoading(false);
      return;
    }

    let isActive = true;
    setEmailCheckLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const exists = await UserService.checkEmail(trimmedEmail);
        if (isActive) {
          setEmailExists(exists);
          if (exists) {
            setUserName('');
            setUserWhatsApp('');
            setErrors((prev) => ({ ...prev, userName: '', userWhatsApp: '' }));
          }
        }
      } catch (err) {
        if (isActive) {
          setEmailExists(null);
        }
      } finally {
        if (isActive) {
          setEmailCheckLoading(false);
        }
      }
    }, 400);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [userEmail]);

  const validateForm = () => {
    const newErrors = {
      userName: '',
      userEmail: '',
      userWhatsApp: '',
      eventDate: '',
      eventType: '',
      invites: '',
      acceptTerms: '',
    };
    let isValid = true;

    if (!userEmail.trim()) {
      newErrors.userEmail = 'E-mail é obrigatório';
      isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(userEmail)) {
      newErrors.userEmail = 'E-mail inválido';
      isValid = false;
    }

    if (emailExists === null) {
      newErrors.userEmail = newErrors.userEmail || 'Verifique o e-mail';
      isValid = false;
    }

    if (emailExists === false && !userName.trim()) {
      newErrors.userName = 'Nome é obrigatório';
      isValid = false;
    }

    if (emailExists === false && !userWhatsApp.trim()) {
      newErrors.userWhatsApp = 'WhatsApp é obrigatório';
      isValid = false;
    }

    const filterEventDateInput = searchParams.get('dataHora')
      ? dayjs(searchParams.get('dataHora')).format('YYYY-MM-DD')
      : '';
    const eventDateValue = filterEventDateInput || eventDateInput || '';
    if (!eventDateValue) {
      newErrors.eventDate = 'Data do evento é obrigatória';
      isValid = false;
    }

    const searchEventId = searchParams.get('evento');
    const selectedEventId =
      selectedEvento?.id !== undefined && selectedEvento?.id !== null
        ? Number(selectedEvento.id)
        : null;
    const hasEventType =
      Number.isFinite(selectedEventId) ||
      (searchEventId ? Number.isFinite(Number(searchEventId)) : false);
    if (!hasEventType) {
      newErrors.eventType = 'Tipo de evento é obrigatório';
      isValid = false;
    }

    const invitesValue = Number(invitesInput || searchParams.get('convidados'));
    if (!Number.isFinite(invitesValue) || invitesValue <= 0) {
      newErrors.invites = 'Número de convidados é obrigatório';
      isValid = false;
    }

    if (!acceptTerms) {
      newErrors.acceptTerms = 'Aceite os termos para continuar';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const isSubmitDisabled = () => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const filterEventDateInput = searchParams.get('dataHora')
      ? dayjs(searchParams.get('dataHora')).format('YYYY-MM-DD')
      : '';
    const eventDateValue = filterEventDateInput || eventDateInput || '';
    const invitesValue = Number(invitesInput || searchParams.get('convidados'));
    const hasInvitesValue = Number.isFinite(invitesValue) && invitesValue > 0;
    const searchEventId = searchParams.get('evento');
    const selectedEventId =
      selectedEvento?.id !== undefined && selectedEvento?.id !== null
        ? Number(selectedEvento.id)
        : null;
    const hasEventType =
      Number.isFinite(selectedEventId) ||
      (searchEventId ? Number.isFinite(Number(searchEventId)) : false);
    const requiresUserDetails = emailExists === false;
    return (
      loading ||
      success ||
      !userEmail.trim() ||
      !emailRegex.test(userEmail) ||
      emailCheckLoading ||
      emailExists === null ||
      (requiresUserDetails && !userName.trim()) ||
      (requiresUserDetails && !userWhatsApp.trim()) ||
      !eventDateValue ||
      !hasEventType ||
      !hasInvitesValue ||
      !acceptTerms
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      const invitesValue = Number(invitesInput || searchParams.get('convidados'));
      const normalizedInvites = Number.isFinite(invitesValue) ? invitesValue : 0;
      const searchEventId = searchParams.get('evento');
      const selectedEventId =
        selectedEvento?.id !== undefined && selectedEvento?.id !== null
          ? Number(selectedEvento.id)
          : null;
      const eventTypeId = selectedEventId ?? (searchEventId ? Number(searchEventId) : null);
      const whatsAppDigits = userWhatsApp.replace(/\D/g, '');
      const normalizedWhatsApp = whatsAppDigits.startsWith('55')
        ? whatsAppDigits
        : `55${whatsAppDigits}`;
      const filterEventDateInput = searchParams.get('dataHora')
        ? dayjs(searchParams.get('dataHora')).format('YYYY-MM-DD')
        : '';
      const payload = {
        userName: emailExists === false ? userName : '',
        userEmail,
        userWhatsApp: emailExists === false ? normalizedWhatsApp : '',
        description: description || null,
        eventDate: filterEventDateInput || eventDateInput || '',
        invites: normalizedInvites,
        hasAirConditioner: searchParams.get('ar') === 'true',
        hasBuffet: searchParams.get('buffet') === 'true',
        hasParking: searchParams.get('estacionamento') === 'true',
        hasOutdoor: searchParams.get('aoArLivre') === 'true',
        hasSound: searchParams.get('som') === 'true',
        hasRoom: searchParams.get('salas') === 'true',
        places: espacos.map((e) => e.id),
        eventTypeId: Number.isFinite(eventTypeId) ? eventTypeId : null,
      };

      // Enviar solicitação para API
      await AvailabilityRequestService.submit(payload);

      setSuccess(true);
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);
    } catch (err: any) {
      setError(err?.data?.detail || 'Erro ao enviar solicitação. Tente novamente.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUserName('');
    setUserEmail('');
    setUserWhatsApp('');
    setDescription('');
    setInvitesInput(searchParams.get('convidados') ? String(searchParams.get('convidados')) : '');
    setEventDateInput(
      searchParams.get('dataHora') ? dayjs(searchParams.get('dataHora')).format('YYYY-MM-DD') : '',
    );
    setSuccess(false);
    setError('');
    setErrors({
      userName: '',
      userEmail: '',
      userWhatsApp: '',
      eventDate: '',
      eventType: '',
      invites: '',
      acceptTerms: '',
    });
    setSelectedEvento(null);
    setAcceptTerms(false);
    setEmailExists(null);
    setEmailCheckLoading(false);
  };

  const getFilterDisplay = () => {
    const filters = [];
    if (filterNames.cidade) filters.push(`Cidade: ${filterNames.cidade}`);
    if (filterNames.regiao) filters.push(`Região: ${filterNames.regiao}`);
    if (filterNames.bairro) filters.push(`Bairro: ${filterNames.bairro}`);
    if (filterNames.evento) filters.push(`Evento: ${filterNames.evento}`);
    if (filterNames.espaco) filters.push(`Tipo de Espaço: ${filterNames.espaco}`);
    if (searchParams.get('convidados'))
      filters.push(`Convidados: ${searchParams.get('convidados')}`);
    return filters;
  };

  const eventDate = eventDateInput ? dayjs(eventDateInput).format('DD/MM/YYYY') : 'Não informada';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isSmallMobile}
      PaperProps={{
        sx: {
          borderRadius: isSmallMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Solicitar Interesse
        </Typography>
        <Button onClick={onClose} size="small" sx={{ minWidth: 'auto', color: 'text.secondary' }}>
          <CloseIcon />
        </Button>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 2 }}>
        {success ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              gap: 2,
            }}
          >
            <Typography variant="h5" fontWeight="bold" color="success.main">
              ✓ Solicitação Enviada
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Sua solicitação foi recebida com sucesso. Entraremos em contato em breve!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Resumo de Filtros */}
            <Paper elevation={0} sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                Resumo da Busca
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {getFilterDisplay().map((filter, idx) => (
                  <Chip key={idx} label={filter} size="small" variant="outlined" />
                ))}
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Mantém o tipo de evento igual ao filtro quando já houver evento filtrado */}
                <Autocomplete
                  sx={{ my: 1 }}
                  options={eventos}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                  value={selectedEvento}
                  onChange={(event, newValue) => {
                    setSelectedEvento(newValue);
                    updateSearchParamsDebounced((params) => {
                      if (newValue?.id) {
                        params.set('evento', String(newValue.id));
                      } else {
                        params.delete('evento');
                      }
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de Evento"
                      required
                      size="small"
                      placeholder="Selecione o tipo de evento"
                      error={!!errors.eventType}
                      helperText={errors.eventType}
                    />
                  )}
                  noOptionsText="Nenhum evento encontrado"
                  disabled={loading}
                />
                <TextField
                  label="Data do evento"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={eventDateInput}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setEventDateInput(nextValue);
                    updateSearchParamsDebounced((params) => {
                      if (nextValue) {
                        params.set('dataHora', nextValue);
                      } else {
                        params.delete('dataHora');
                      }
                    });
                  }}
                  error={!!errors.eventDate}
                  helperText={errors.eventDate}
                  required
                  disabled={loading}
                />
                <TextField
                  label="Número de convidados"
                  type="number"
                  size="small"
                  value={invitesInput}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={!!errors.invites}
                  helperText={errors.invites}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setInvitesInput(nextValue);
                    updateSearchParamsDebounced((params) => {
                      if (nextValue) {
                        params.set('convidados', nextValue);
                      } else {
                        params.delete('convidados');
                      }
                    });
                  }}
                  inputProps={{ min: 1 }}
                />
              </Box>
            </Paper>

            {/* Lista de Espaços */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                Espaços Filtrados ({espacos.length})
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: '#fafafa',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {espacos.length > 0 ? (
                  <Box sx={{ p: 1 }}>
                    {espacos.map((espaco, idx) => (
                      <Box
                        key={espaco.id}
                        sx={{
                          py: 1,
                          px: 1.5,
                          borderBottom: idx < espacos.length - 1 ? '1px solid #e0e0e0' : 'none',
                          fontSize: '0.85rem',
                        }}
                      >
                        <Typography variant="caption" fontWeight="500">
                          {espaco.fantasyName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {espaco.address}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Nenhum espaço encontrado
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Formulário de Dados */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Seus Dados
              </Typography>

              <TextField
                label="E-mail"
                variant="outlined"
                size="small"
                fullWidth
                type="email"
                required
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                error={!!errors.userEmail}
                helperText={
                  errors.userEmail ||
                  (emailCheckLoading
                    ? 'Verificando e-mail...'
                    : emailExists
                    ? 'Utilizaremos os dados cadastrados neste e-mail'
                    : 'Preencha os dados para contato')
                }
                disabled={loading}
              />

              {emailExists === false && (
                <TextField
                  label="Nome Completo"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  error={!!errors.userName}
                  helperText={errors.userName}
                  disabled={loading}
                />
              )}

              {emailExists === false && (
                <PatternFormat
                  value={userWhatsApp}
                  onValueChange={(values: any) => setUserWhatsApp(values.value)}
                  format="+55 (##) #####-####"
                  mask="_"
                  customInput={TextField}
                  label="WhatsApp"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  error={!!errors.userWhatsApp}
                  helperText={errors.userWhatsApp}
                  disabled={loading}
                />
              )}
              <TextField
                label="Descrição (opcional)"
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva detalhes adicionais sobre sua solicitação..."
                disabled={loading}
              />

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      size="small"
                      disabled={loading}
                    />
                  }
                  label={
                    <span>
                      Eu aceito os{' '}
                      <a
                        href="https://umespaco.com/como-funciona/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#1f75cf', fontWeight: 600, textDecoration: 'none' }}
                      >
                        Termos de Serviço e a Política de Privacidade
                      </a>
                    </span>
                  }
                />
                {errors.acceptTerms && (
                  <Typography variant="caption" color="error" display="block">
                    {errors.acceptTerms}
                  </Typography>
                )}
              </Box>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitDisabled()}
          sx={{ minWidth: '120px' }}
        >
          {loading ? <CircularProgress size={20} /> : 'Enviar Solicitação'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailabilityRequestModal;
